<script setup lang="ts">
//#region imports
import { computed, nextTick, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import { required, requiredIf, minLength, maxLength, ipAddress } from '@vuelidate/validators';
import { useApi } from '@/api';
import { useValidate } from '@/composables/useValidate';
import { useRulesValidator } from '@/composables/useRulesValidator';
import type {
  PostDomainsManagementPlatformServersData,
  PostDomainsManagementPlatformServersResponse,
} from '@/types/openapi-ts';
import type { EmitsToListeners } from '@/types/core';
import type { OverrideFields } from '@/types/util';
//#endregion

//#region types
interface Props {
  platformServerId: string | null,
}

interface  Emits {
  'success': [row: PostDomainsManagementPlatformServersResponse['payload'], value: boolean],
  'close': [],
}

export interface PlatformServerParams {
  data?: Props;
  listeners?: EmitsToListeners<Emits>
}

type FormType = OverrideFields<PostDomainsManagementPlatformServersData['body'], {
  id?: string
  additional_ipv4_addresses?: string;
  additional_ipv6_addresses?: string;
  main_ipv4_address?: string;
  main_ipv6_address?: string;
}>;
//#endregion

//#region define
const props = defineProps<Props>();
const emit = defineEmits<Emits>();
//#endregion

//#region composables
const api = useApi();
const { t } = useI18n();
const { ipv6Address, ipv6Addresses, ipv4Addresses } = useRulesValidator();
const { checkField, isInvalidField, isInvalidForm } = useValidate({
  rules: getRules(),
  form: computed(() => {
    return form.value || {};
  }),
});
//#endregion

//#region state
const isVisible = ref(false);
const isLoading = ref(false);
const errors = ref({});

const form = ref<FormType>({
  name: '',
  description: '',
  can_be_picked_automatically: true,
  main_ipv4_address: '',
  additional_ipv4_addresses: '',
  main_ipv6_address: '',
  additional_ipv6_addresses: '',
});
//#endregion

//#region computed
const isEdit = computed(() => {
  return props.platformServerId;
});
const pPopupTitle = computed(() => {
  return `${t(`domainManagement.popup.${isEdit.value ? 'edit' : 'add'}`)} ${t('domainManagement.popup.platformServer')}`;
});
//#endregion

//#region methods
function getRules() {
  return {
    name: {
      required,
      minLength: minLength(1),
      maxLength: maxLength(255),
    },
    description: {
      required,
      minLength: minLength(1),
      maxLength: maxLength(1024),
    },
    main_ipv4_address: {
      required: requiredIf(() => !isEdit.value),
      ipAddress,
    },
    main_ipv6_address: {
      ipv6Address,
    },
    additional_ipv4_addresses: {
      ipv4Addresses,
    },
    additional_ipv6_addresses: {
      ipv6Addresses,
    },
  };
}

async function open() {

  if(isEdit.value) {
    const { data: { payload } } = await api.getPlatformServerById(props.platformServerId!);
    const {
      name,
      description,
      can_be_picked_automatically,
    } = payload;

    form.value = {
      name,
      description,
      can_be_picked_automatically,
    };
  }

  await nextTick();
  isVisible.value = true;
}

function handleClose() {
  emit('close');
  isVisible.value = false;
}

function handleSave() {
  isLoading.value = true;
  errors.value = {};
  let isCreate = false;
  let promise;

  if(isEdit.value) {
    promise = api.updatePlatformServer(props.platformServerId!, form.value);
  } else {
    isCreate = true;
    const payload = {
      ...form.value,
    } as unknown as PostDomainsManagementPlatformServersData['body'];

    payload.additional_ipv4_addresses = form.value.additional_ipv4_addresses ? form.value.additional_ipv4_addresses.trim().split('\n').map((ip) => ip.trim()) : [];
    payload.main_ipv6_address = payload.main_ipv6_address || null;
    payload.additional_ipv6_addresses = form.value.additional_ipv6_addresses ? form.value.additional_ipv6_addresses.trim().split('\n').map((ip) => ip.trim()) : [];
    promise = api.createPlatformServer(payload);
  }

  promise.then((res) => {
    const { data: { payload } } = res;
    emit('success', payload, isCreate);
    isVisible.value = false;
  }).catch((error) => {
    errors.value = error.data.errors;
  }).finally(() => {
    isLoading.value = false;
  });
}
//#endregion

defineExpose({
  open,
});
</script>

<template>
  <Popup
    v-model:is-visible="isVisible"
    class="platform-server-popup"
    :title="pPopupTitle"
    :is-loading="isLoading"
    :width="480"
    @close="handleClose"
  >
    <template v-slot:body>
      <div class="t:space-y-16">
        <UiInput
          v-model="form.name"
          :label="$t(`domainManagement.popup.form.server_name.label`)"
          :placeholder="$t(`domainManagement.popup.form.server_name.placeholder`)"
          required
          autosize
          size="big"
          :is-error="isInvalidField('name')"
          @blur="checkField('name')"
        />
        <UiTextarea
          v-model="form.description"
          :label="$t(`domainManagement.popup.form.server_description.label`)"
          :placeholder="$t(`domainManagement.popup.form.server_description.placeholder`)"
          required
          :rows="3"
          :is-error="isInvalidField('description')"
          @blur="checkField('description')"
        />
        <CardTitle :title="$t(`domainManagement.popup.form.server_auto_pick.label`)">
          <UiSwitch v-model="form.can_be_picked_automatically" />
        </CardTitle>
        <template v-if="!isEdit">
          <UiInput
            v-model="form.main_ipv4_address"
            :label="$t(`domainManagement.popup.form.main_ipv4_address.label`)"
            :placeholder="$t(`domainManagement.popup.form.main_ipv4_address.placeholder`)"
            required
            autosize
            size="big"
            :is-error="isInvalidField('main_ipv4_address')"
            @blur="checkField('main_ipv4_address')"
          />
          <UiTextarea
            v-model="form.additional_ipv4_addresses"
            :label="$t(`domainManagement.popup.form.additional_ipv4_addresses.label`)"
            :placeholder="$t(`domainManagement.popup.form.additional_ipv4_addresses.placeholder`)"
            :rows="3"
            :is-error="isInvalidField('additional_ipv4_addresses')"
            @blur="checkField('additional_ipv4_addresses')"
          />
          <UiInput
            v-model="form.main_ipv6_address"
            :label="$t(`domainManagement.popup.form.main_ipv6_address.label`)"
            :placeholder="$t(`domainManagement.popup.form.main_ipv6_address.placeholder`)"
            autosize
            size="big"
            :is-error="isInvalidField('main_ipv6_address')"
            @blur="checkField('main_ipv6_address')"
          />
          <UiTextarea
            v-model="form.additional_ipv6_addresses"
            :label="$t(`domainManagement.popup.form.additional_ipv6_addresses.label`)"
            :placeholder="$t(`domainManagement.popup.form.additional_ipv6_addresses.placeholder`)"
            :rows="3"
            :is-error="isInvalidField('additional_ipv6_addresses')"
            @blur="checkField('additional_ipv6_addresses')"
          />
        </template>
      </div>
    </template>
    <template v-slot:footer>
      <div class="t:justify-end t:flex t:space-x-10">
        <UiButton
          type="negative"
          size="big"
          icon="times"
          :disabled="isLoading"
          @click="handleClose"
        >
          {{ $t(`domainManagement.popup.cancel`) }}
        </UiButton>
        <UiButton
          filled
          size="big"
          icon="check"
          :disabled="isInvalidForm || isLoading"
          @click="handleSave"
        >
          {{ $t(`domainManagement.popup.save`) }}
        </UiButton>
      </div>
    </template>
  </Popup>
</template>
